import React, { useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  Stack,
  ScrollArea,
  UnstyledButton,
  Tooltip,
} from '@mantine/core';
import { IconChevronRight, IconChevronDown, IconEye, IconEyeOff } from '@tabler/icons';
import { FilterCategory } from '../types';
import { ChildFilterItem } from '../components/left-panel/ChildFiltersDropdown';
import ChildFiltersDropdownManageFilters from './ChildFiltersDropdownManageFilters';


/** Re-export for consumers of LeftPanel */
export type { ChildFilterItem };

export interface LeftPanelManageFiltersProps {
  ideaCategories: FilterCategory[];
  evidenceCategories: FilterCategory[];
  getCategoryTooltip: (categoryId: string, disabled: boolean) => string;
  getCategoryIcon: (categoryId: string) => React.ReactNode;
  handleCategorySelect: (categoryId: string, categoryType: 'idea' | 'evidence') => void;
  selectedCategory: string;
  childFilters: ChildFilterItem[];
  categoryIdsWithChildren: Set<string>;
  searchInput: React.ReactNode;
  handleChildFilterSelect: (childFilterId: string) => void;
  middlePanelSearch: string;
  isFromIdeas?: boolean;
  filterVisibility: Record<string, boolean>;
  onToggleVisibility: (filterId: string) => void;
}

const LeftPanelManageFilters: React.FC<LeftPanelManageFiltersProps> = ({
  ideaCategories,
  evidenceCategories,
  getCategoryTooltip,
  getCategoryIcon,
  handleCategorySelect,
  selectedCategory,
  childFilters,
  categoryIdsWithChildren,
  searchInput,
  handleChildFilterSelect,
  middlePanelSearch,
  isFromIdeas = false,
  filterVisibility,
  onToggleVisibility,
}) => {
  const sortedIdeaCategories = useMemo(() => {
    const visible = ideaCategories.filter(c => filterVisibility[c.id] !== false);
    const hidden = ideaCategories.filter(c => filterVisibility[c.id] === false);
    return [...visible, ...hidden];
  }, [ideaCategories, filterVisibility]);

  const sortedEvidenceCategories = useMemo(() => {
    const visible = evidenceCategories.filter(c => filterVisibility[c.id] !== false);
    const hidden = evidenceCategories.filter(c => filterVisibility[c.id] === false);
    return [...visible, ...hidden];
  }, [evidenceCategories, filterVisibility]);

  return (
    <Box className="filter-modal-left-panel">
      <Box
        pt="md"
        pb="md"
        pl="md"
        pr={0}
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ScrollArea sx={{ flex: 1, minHeight: 0 }}>
          <Box pr="md">
            {isFromIdeas && (
              <Box>
                <Text size="xs" fw={600} c="#9ca3af" mb={10} tt="uppercase" lts={0.5}>
                  Idea filters
                </Text>
                <Stack spacing={4}>
                  {sortedIdeaCategories.map(
                    (category) =>
                      !category.disabled && (
                        <Box key={category.id}>
                          <Tooltip
                            label={getCategoryTooltip(category.id, category.disabled ?? false)}
                            position="top"
                            multiline
                            width={230}
                            withArrow
                            disabled={true}
                          >
                            <UnstyledButton
                              onClick={() => !category.disabled && handleCategorySelect(category.id)}
                              disabled={category.disabled}
                              className={`filter-category-button ${category.disabled ? 'disabled' : ''}`}
                              style={{ justifyContent: 'space-between' }}
                              sx={{
                                borderRadius: 5,
                                outline: 'none',
                                '&:focus, &:focus-visible': { outline: 'none', boxShadow: 'none' },
                              }}
                            >
                              <Flex align="center" gap={8}>
                                {/* {getCategoryIcon(category.id)} */}
                                {category.label}
                                {categoryIdsWithChildren.has(category.id) && (
                                  <Flex align="center" sx={{ flexShrink: 0 }}>
                                    {category.active ? (
                                      <IconChevronDown size={13} color="#6c757d" stroke={2} />
                                    ) : (
                                      <IconChevronRight size={13} color="#6c757d" stroke={2} />
                                    )}
                                  </Flex>
                                )}
                              </Flex>
                              <Box
                                component="span"
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  onToggleVisibility(category.id);
                                }}
                                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', marginRight: 8 }}
                              >
                                {filterVisibility[category.id] !== false ? (
                                  <IconEye size={18} color="#9ca3af" stroke={1.5} />
                                ) : (
                                  <IconEyeOff size={18} color="#9ca3af" stroke={1.5} />
                                )}
                              </Box>
                            </UnstyledButton>
                          </Tooltip>
                          {category.id === selectedCategory && childFilters.length > 0 && (
                            <ChildFiltersDropdownManageFilters
                              searchInput={searchInput}
                              childFilters={childFilters}
                              middlePanelSearch={middlePanelSearch}
                              handleChildFilterSelect={handleChildFilterSelect}
                              parentId={category.id}
                              filterVisibility={filterVisibility}
                              onToggleVisibility={onToggleVisibility}
                            />
                          )}
                        </Box>
                      )
                  )}
                </Stack>
              </Box>
            )}

            <Box pt={isFromIdeas ? 20 : 0}>
              <Text size="xs" fw={600} c="#9ca3af" mb={10} tt="uppercase" lts={0.5}>
                Evidence filters
              </Text>
              <Stack spacing={4}>
                {sortedEvidenceCategories.map(
                  (category) =>
                    !category.disabled && (
                      <Box key={category.id}>
                        <Tooltip
                          label={getCategoryTooltip(category.id, category.disabled ?? false)}
                          position="top"
                          multiline
                          width={230}
                          withArrow
                          disabled={true}
                        >
                          <UnstyledButton
                            onClick={() => !category.disabled && handleCategorySelect(category.id)}
                            disabled={category.disabled}
                            className={`filter-category-button ${category.disabled ? 'disabled' : ''}`}
                            style={{ justifyContent: 'space-between' }}
                            sx={{
                              borderRadius: 8,
                              outline: 'none',
                              '&:focus, &:focus-visible': { outline: 'none', boxShadow: 'none' },
                            }}
                          >
                            <Flex align="center" gap={8}>
                              {/* {getCategoryIcon(category.id)} */}
                              {category.label}
                              {categoryIdsWithChildren.has(category.id) && (
                                <Flex align="center" sx={{ flexShrink: 0 }}>
                                  {category.active ? (
                                    <IconChevronDown size={12} color="#6c757d" stroke={2} />
                                  ) : (
                                    <IconChevronRight size={12} color="#6c757d" stroke={2} />
                                  )}
                                </Flex>
                              )}
                            </Flex>
                            <Box
                              component="span"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                onToggleVisibility(category.id);
                              }}
                              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', marginRight: 8 }}
                            >
                              {filterVisibility[category.id] !== false ? (
                                <IconEye size={18} color="#9ca3af" stroke={1.5} />
                              ) : (
                                <IconEyeOff size={18} color="#9ca3af" stroke={1.5} />
                              )}
                            </Box>
                          </UnstyledButton>
                        </Tooltip>
                        {category.id === selectedCategory && childFilters.length > 0 && (
                          <ChildFiltersDropdownManageFilters
                            searchInput={searchInput}
                            childFilters={childFilters}
                            middlePanelSearch={middlePanelSearch}
                            handleChildFilterSelect={handleChildFilterSelect}
                            parentId={category.id}
                            filterVisibility={filterVisibility}
                            onToggleVisibility={onToggleVisibility}
                          />
                        )}
                      </Box>
                    )
                )}
              </Stack>
            </Box>
          </Box>
        </ScrollArea>
      </Box>
    </Box>
  );
};

export default LeftPanelManageFilters;